// What travels on the queues (architecture plan §8.13, §8.14; build plan P8). The job carries the
// redacted request itself, because the agent instance cannot read the database (§12.1); the
// result carries the typed proposal. Deduplication ids derive from the job event id at both ends,
// so a redelivered job that produces a second result is collapsed by the inbox, not applied twice.

import { z } from "zod";
import { BUSINESS_IMPACTS } from "./intake";
import { ProposalSchema } from "./proposal";
import { PublicIdSchema } from "./public-id";

export const INBOX_SOURCE_TRIAGE_RESULTS = "triage-results";

/** The intake fields as the agent may see them: already redacted, never contact details. */
export const TriageJobFieldsSchema = z.object({
  subject: z.string(),
  description: z.string(),
  department: z.string(),
  location: z.string(),
  onsiteOrRemote: z.enum(["onsite", "remote"]),
  affectedSystemOrDevice: z.string().default(""),
  onset: z.string().default(""),
  errorText: z.string().default(""),
  troubleshootingAttempted: z.string().default(""),
  businessImpact: z.enum(BUSINESS_IMPACTS),
  neededBy: z.string().nullable().default(null),
  followUpAnswers: z.record(z.string(), z.unknown()).default({}),
});

/** One triage job. Keys the agent does not need are dropped at this boundary (rule 10). */
export const TriageJobSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  /** The outbox event id: the FIFO deduplication id, and what the result derives its id from. */
  jobEventId: z.uuid(),
  requestId: z.uuid(),
  publicId: PublicIdSchema,
  category: z.string().min(1),
  submittedAt: z.iso.datetime(),
  fields: TriageJobFieldsSchema,
  /**
   * Present on a re-run (§7.6, build plan P10/P11): the prior run and the new words, so the
   * second pass does not repeat the first pass's dead ends. Data, never instruction (rule 10).
   */
  context: z
    .object({
      priorRunId: z.uuid().optional(),
      priorOutcome: z.string().optional(),
      reviewerNote: z.string().max(4000).optional(),
      feedbackComment: z.string().max(4000).optional(),
    })
    .optional(),
});
export type TriageJob = z.infer<typeof TriageJobSchema>;
export type TriageJobInput = z.input<typeof TriageJobSchema>;

export const TriageResultMessageSchema = z.object({
  kind: z.literal("triage.result"),
  schemaVersion: z.literal(1).default(1),
  proposal: ProposalSchema,
  producedAt: z.iso.datetime(),
  agent: z
    .object({
      version: z.string(),
      model: z.string().optional(),
      promptVersion: z.string().optional(),
    })
    .optional(),
});
export type TriageResultMessage = z.infer<typeof TriageResultMessageSchema>;
export type TriageResultMessageInput = z.input<typeof TriageResultMessageSchema>;

/** Everything the results queue may carry; later phases add remediation updates here. */
export const ResultsMessageSchema = z.discriminatedUnion("kind", [TriageResultMessageSchema]);
export type ResultsMessage = z.infer<typeof ResultsMessageSchema>;

export function triageJobDedupeId(jobEventId: string): string {
  return jobEventId;
}

/** Derived from the job event id, never from a fresh run id (§8.14). */
export function triageResultDedupeId(jobEventId: string): string {
  return `result:${jobEventId}`;
}
